// /dashboard/risk-assessment landing page.
//
// This is the user-facing portal for the Risk Intelligence Suite. It
// inherits the global app shell (Layout + Sidebar) from src/shared/ui
// so visual consistency with the dashboard is automatic.
//
// Three responsibilities:
//   1. Pitch the suite (what it does, why it matters).
//   2. Surface a "Take / Resume Assessment" CTA whose label and
//      behaviour adapt based on the user's persisted state:
//        * No row at all          -> "Take the Assessment".
//        * Draft only             -> "Resume Assessment" with
//                                    progress hint.
//        * Completed assessment   -> "View Latest Results" + a
//                                    secondary "Retake" button.
//   3. Surface the live Portfolio Stress Test and Goal-Based Risk
//      Alignment Tool as active tool cards so the full Risk
//      Intelligence Suite is one click away from this landing page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Layout, Sidebar } from '../../shared/ui';
import { riskApi, type PersistedAssessmentResult } from './api/riskApi';

// --- Inline icons (no lucide-react dep, matches Sidebar pattern) ----------
const ShieldIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-7 w-7"
        aria-hidden
    >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
    </svg>
);
const TrendingUpIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-6 w-6"
        aria-hidden
    >
        <path d="M3 17 9 11l4 4 8-8" />
        <path d="M14 7h7v7" />
    </svg>
);
// Concentric-rings target glyph — visually distinct from the
// shield (Risk Profiler) and the trend arrow (Stress Test) so the
// three active tool cards read as siblings rather than duplicates.
const TargetIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-6 w-6"
        aria-hidden
    >
        <circle cx={12} cy={12} r={10} />
        <circle cx={12} cy={12} r={6} />
        <circle cx={12} cy={12} r={2} />
    </svg>
);
const ArrowRightIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
        aria-hidden
    >
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
);

// ---------------------------------------------------------------------------
// State machine -- "what's the user's persisted assessment look like?"
// ---------------------------------------------------------------------------

type State =
    | { kind: 'loading' }
    | { kind: 'none' }
    | { kind: 'draft'; answeredCount: number; total: number; result: PersistedAssessmentResult }
    | { kind: 'complete'; result: PersistedAssessmentResult }
    | { kind: 'error'; message: string };

const TOTAL_QUESTIONS = 43; // 10 RTQ + 5 FC + 20 behavioral + 8 style

function answeredCount(result: PersistedAssessmentResult): number {
    const r = result.raw_answers;
    if (!r) return 0;
    return (
        Object.keys(r.rtq_answers ?? {}).length +
        Object.keys(r.behavioral_answers ?? {}).length +
        Object.keys(r.fc_answers ?? {}).length +
        Object.keys(r.style_answers ?? {}).length
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PrimaryToolCard = ({ state }: { state: State }) => {
    let ctaLabel = 'Take the Assessment';
    let ctaHref = '/dashboard/risk-assessment/test';
    let secondaryAction: { label: string; href: string } | null = null;
    let statusLine: string | null = null;

    if (state.kind === 'draft') {
        ctaLabel = 'Resume Assessment';
        statusLine = `Draft saved — ${state.answeredCount} of ${state.total} questions answered.`;
    } else if (state.kind === 'complete') {
        ctaLabel = 'View Latest Results';
        ctaHref = '/dashboard/risk-assessment/test?view=results';
        secondaryAction = {
            label: 'Retake',
            href: '/dashboard/risk-assessment/test?retake=1',
        };
        const profile = state.result.risk_profile ?? '—';
        statusLine = `Latest profile: ${profile}.`;
    }

    return (
        <article className="surface-card flex flex-col">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
                <ShieldIcon />
            </div>
            <h3 className="section-title text-slate-900 dark:text-white">
                Comprehensive Risk Profiler
            </h3>
            <p className="mt-2 text-sm muted">
                Analyses Risk Tolerance, Financial Capacity and Behavioral Bias
                across <strong className="text-slate-900 dark:text-white">43 data points</strong> to
                produce an institutional-grade allocation recommendation.
            </p>

            {statusLine && (
                <p className="mt-4 rounded-lg bg-slate-100 dark:bg-slate-800/60 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {statusLine}
                </p>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
                <Link
                    to={ctaHref}
                    className="inline-flex flex-1 items-center justify-between rounded-xl bg-slate-900 dark:bg-slate-200 px-4 py-2.5 text-sm font-bold text-white dark:text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                >
                    {ctaLabel} <ArrowRightIcon />
                </Link>
                {secondaryAction && (
                    <Link
                        to={secondaryAction.href}
                        className="inline-flex items-center justify-center rounded-xl border-2 border-slate-300 dark:border-slate-700 px-4 py-2.5 text-sm font-bold text-slate-900 dark:text-white transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                    >
                        {secondaryAction.label}
                    </Link>
                )}
            </div>
        </article>
    );
};

// Live, clickable tool card — same visual rhythm as ``PrimaryToolCard``
// (surface-card + emerald accent + arrow CTA) but without the
// state-machine wiring because this tool is always available. Used for
// the Portfolio Stress Test entry below the Risk Profiler.
const ActiveToolCard = ({
    icon,
    title,
    blurb,
    ctaLabel,
    ctaHref,
}: {
    icon: React.ReactNode;
    title: string;
    blurb: string;
    ctaLabel: string;
    ctaHref: string;
}) => (
    <article className="surface-card flex flex-col">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
            {icon}
        </div>
        <h3 className="section-title text-slate-900 dark:text-white">
            {title}
        </h3>
        <p className="mt-2 text-sm muted">{blurb}</p>
        <div className="mt-6">
            <Link
                to={ctaHref}
                className="inline-flex items-center justify-between gap-2 rounded-xl bg-slate-900 dark:bg-slate-200 px-4 py-2.5 text-sm font-bold text-white dark:text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
            >
                {ctaLabel} <ArrowRightIcon />
            </Link>
        </div>
    </article>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const RiskLandingPage = () => {
    const [state, setState] = useState<State>({ kind: 'loading' });

    useEffect(() => {
        let cancelled = false;
        riskApi
            .getLatestResults()
            .then((result) => {
                if (cancelled) return;
                if (!result) {
                    setState({ kind: 'none' });
                    return;
                }
                if (result.risk_scores) {
                    setState({ kind: 'complete', result });
                    return;
                }
                setState({
                    kind: 'draft',
                    answeredCount: answeredCount(result),
                    total: TOTAL_QUESTIONS,
                    result,
                });
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('[RiskLandingPage] /results failed', err);
                setState({
                    kind: 'error',
                    message:
                        'We could not load your risk-assessment status. The questionnaire still works — try the Take Assessment button.',
                });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Risk Intelligence Suite"
            subtitle="Institutional-grade behavioral analysis and capacity modeling."
        >
            {state.kind === 'error' && (
                <p className="surface-card-error mb-6">{state.message}</p>
            )}

            <section
                aria-label="Risk-assessment tools"
                className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
            >
                {/* Primary tool — adapts copy + CTA based on persisted
                    state. Renders a pulse skeleton while we wait for
                    /results so the layout never jumps. */}
                {state.kind === 'loading' ? (
                    <div className="surface-card-skeleton h-64">
                        <div className="skeleton-line-lg w-1/3 mb-4" />
                        <div className="skeleton-line w-full mb-2" />
                        <div className="skeleton-line w-2/3" />
                    </div>
                ) : (
                    <PrimaryToolCard state={state} />
                )}

                {/* Live tool — Portfolio Stress Test. Connects the
                    risk-assessment's recommended allocation to real
                    market-shock scenarios so the user can see "what
                    happens to my portfolio if things go wrong?". */}
                <ActiveToolCard
                    icon={<TrendingUpIcon />}
                    title="Portfolio Stress Test & Scenario Simulator"
                    blurb="See how your recommended allocation behaves under shocks like inflation spikes, recessions and rate cuts. Pick a scenario, set your amount and horizon, watch the chart update live."
                    ctaLabel="Open simulator"
                    ctaHref="/dashboard/portfolio-stress-test"
                />

                {/* Live tool — Goal-Based Risk Alignment. Sits to the
                    right of the Stress Test card so the suite reads as
                    "profile → stress → goal" left-to-right. Sub-text
                    mirrors the rhythm of the Stress Test blurb. */}
                <ActiveToolCard
                    icon={<TargetIcon />}
                    title="Goal Alignment"
                    blurb="Explore how recommended asset allocation can hit your financial goal that you set for yourself across thousands of real-world market scenarios."
                    ctaLabel="Open Goal Alignment"
                    ctaHref="/dashboard/goal-based-risk-alignment-tool"
                />
            </section>

            {/* Why-this-matters strip — keeps the page from feeling
                empty before the future modules ship and reinforces the
                sales pitch for non-power-users. */}
            <section className="mt-10 surface-card">
                <h2 className="section-title mb-3">
                    Why every dimension matters
                </h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3 text-sm">
                    <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                            Tolerance ≠ Capacity
                        </h4>
                        <p className="mt-1 muted">
                            Your <em>willingness</em> to take risk and your
                            <em> ability</em> to absorb losses are separate
                            constraints. The optimal portfolio respects both.
                        </p>
                    </div>
                    <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                            Behavior is the alpha leak
                        </h4>
                        <p className="mt-1 muted">
                            Forty years of research show investors lose more
                            to their own biases than to fees. We map yours
                            across two cognitive buckets so you can see them
                            coming.
                        </p>
                    </div>
                    <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                            Active vs Passive
                        </h4>
                        <p className="mt-1 muted">
                            The same risk profile expresses very differently
                            for an indexer than for a stock-picker. We let
                            you tilt the recommendation to the style you
                            actually run.
                        </p>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default RiskLandingPage;
